import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendar,
  faCalendar as faCalendarIcon,
  faChartLine,
  faChevronDown,
  faSignOutAlt,
  faStore,
  faUser,
  faUsers,
} from '@fortawesome/free-solid-svg-icons';
import { useParams } from 'react-router-dom';
import { useAuthentication } from '@features/auth/useAuthentication.ts';
import { useCurrentSalonStore } from '@/stores/useCurrentSalonStore';
import SalonSwitcher from '@components/SalonSwitcher.tsx';

interface AppHeaderProps {
  title?: string;
  breadcrumb?: Array<{ label: string; href?: string }>;
}

const initials = (firstName?: string, lastName?: string) =>
  `${(firstName ?? '').charAt(0)}${(lastName ?? '').charAt(0)}`.toUpperCase() || '?';

const AppHeader: React.FC<AppHeaderProps> = ({ title, breadcrumb }) => {
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { loggedUser, logout, canManageSalon, canManageStaff } = useAuthentication();
  const { currentSalon } = useCurrentSalonStore();
  const { entityId: urlEntityId } = useParams<{ entityId?: string }>();
  // Fallback chain: URL → currentSalon (may be null briefly after login) →
  // user's accessRights (always populated once whoAmI returns).
  const salonId = urlEntityId
    || currentSalon?.salonId
    || loggedUser?.accessRights?.salonId
    || undefined;
  const fullName = `${loggedUser?.firstName ?? ''} ${loggedUser?.lastName ?? ''}`.trim() || '—';
  const email = loggedUser?.email ?? '';
  const roleLabel = loggedUser?.isAdmin
    ? 'Administrátor'
    : currentSalon?.role
      ? currentSalon.role.charAt(0) + currentSalon.role.slice(1).toLowerCase()
      : 'Uživatel';

  const profileHref = salonId ? `/app/${salonId}/profile` : '#';
  const calendarHref = salonId ? `/app/${salonId}/calendar` : '#';
  const performanceHref = salonId ? `/app/${salonId}/performance` : '#';
  const salonProfileHref = salonId ? `/app/${salonId}/salon-profile` : '#';
  const staffHref = salonId ? `/app/${salonId}/staff` : '#';

  return (
    <>
      <header className="sticky top-0 z-20 bg-white shadow-sm border-b border-gray-200">
        <div className="flex items-center justify-between h-16 px-6">
          {/* Left — breadcrumb */}
          <div className="flex items-center space-x-2 text-sm">
            <FontAwesomeIcon icon={faCalendarIcon} className="text-green-600" />
            {breadcrumb && breadcrumb.length > 0 ? (
              <>
                {breadcrumb.map((item, index) => (
                  <React.Fragment key={index}>
                    {item.href && index < breadcrumb.length - 1 ? (
                      <a href={item.href} className="text-gray-600 hover:text-green-600 transition-colors">
                        {item.label}
                      </a>
                    ) : (
                      <span className={index === breadcrumb.length - 1 ? 'text-green-600 font-medium' : 'text-gray-600'}>{item.label}</span>
                    )}
                    {index < breadcrumb.length - 1 && <FontAwesomeIcon icon={faChevronDown} className="text-gray-400 text-xs rotate-90" />}
                  </React.Fragment>
                ))}
              </>
            ) : (
              <span className="text-green-600 font-medium">{title || 'Dashboard'}</span>
            )}
          </div>

          {/* Right — toolbar */}
          <div className="flex items-center space-x-4">
            <SalonSwitcher />

            {/* User menu */}
            <div className="relative">
              <button
                onClick={() => setUserMenuOpen(open => !open)}
                className="flex items-center space-x-2 p-2 rounded-lg hover:bg-gray-100 transition-all"
              >
                <span className="w-8 h-8 rounded-full bg-green-600 text-white flex items-center justify-center text-sm font-semibold">
                  {initials(loggedUser?.firstName, loggedUser?.lastName)}
                </span>
                <div className="text-left hidden lg:block">
                  <p className="text-sm font-medium text-gray-900">{fullName}</p>
                  <p className="text-xs text-gray-600">{roleLabel}</p>
                </div>
                <FontAwesomeIcon icon={faChevronDown} className="text-xs text-gray-400" />
              </button>

              {userMenuOpen && (
                <div className="absolute right-0 top-12 w-64 bg-white rounded-lg shadow-lg border animate-fadeIn">
                  <div className="p-4 border-b">
                    <div className="flex items-center space-x-3">
                      <span className="w-12 h-12 rounded-full bg-green-600 text-white flex items-center justify-center text-base font-semibold">
                        {initials(loggedUser?.firstName, loggedUser?.lastName)}
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-gray-900 truncate">{fullName}</p>
                        {email && <p className="text-sm text-gray-600 truncate">{email}</p>}
                        <p className="text-xs text-gray-500">{currentSalon?.salonName ?? roleLabel}</p>
                      </div>
                    </div>
                  </div>

                  <div className="py-2">
                    <a href={profileHref} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <FontAwesomeIcon icon={faUser} className="mr-3 text-gray-400" />
                      Můj profil
                    </a>
                    <a href={calendarHref} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                      <FontAwesomeIcon icon={faCalendar} className="mr-3 text-gray-400" />
                      Kalendář
                    </a>
                    {(canManageSalon(salonId) || loggedUser?.isAdmin) && (
                      <a href={performanceHref} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        <FontAwesomeIcon icon={faChartLine} className="mr-3 text-gray-400" />
                        Statistiky výkonu
                      </a>
                    )}
                    {canManageSalon(salonId) && (
                      <>
                        <div className="border-t my-2"></div>
                        <a href={salonProfileHref} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                          <FontAwesomeIcon icon={faStore} className="mr-3 text-gray-400" />
                          Profil salonu
                        </a>
                      </>
                    )}
                    {canManageStaff(salonId) && (
                      <a href={staffHref} className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-100">
                        <FontAwesomeIcon icon={faUsers} className="mr-3 text-gray-400" />
                        Správa zaměstnanců
                      </a>
                    )}
                    <div className="border-t my-2"></div>
                    <button
                      type="button"
                      onClick={logout}
                      className="w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                    >
                      <FontAwesomeIcon icon={faSignOutAlt} className="mr-3" />
                      Odhlásit se
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </header>

      {userMenuOpen && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setUserMenuOpen(false)}
        />
      )}
    </>
  );
};

export default AppHeader;
